import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Wrench } from "lucide-react";
import { toast } from "sonner";

const BAYS = [11, 12, 13, 14, 15];

// Admin/lead action: send a completed part to the Maintenance Clearing area for installer pickup.
export default function MoveToMaintenanceButton({ rework, onDone, size = "default", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const eligible = ["Completed - Finished Rack", "Completed - Maintenance"].includes(rework.status);
  if (!eligible || !["admin", "lead"].includes(user?.role)) return null;

  const move = async (bay) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reworks/${rework.id}/maintenance`, { bay_number: bay });
      toast.success(`${rework.job_number} / ${rework.part_number} → Maintenance Clearing${bay ? ` · Bay ${bay}` : ""}`);
      setOpen(false);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not move part");
    } finally {
      setBusy(false);
    }
  };

  const big = size === "lg";
  return (
    <>
      <Button variant="outline" disabled={busy} onClick={() => setOpen(true)} data-testid={`to-maintenance-${rework.id}`}
        className={`rounded-sm border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 uppercase tracking-widest ${big ? "h-12 px-5 text-xs w-full" : "h-8 px-3 text-[11px]"} ${className}`}>
        <Wrench size={12} className="mr-1.5" /> To Maintenance
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#e6e5df] rounded-sm" data-testid="maintenance-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#232826]">Move to Maintenance Clearing</DialogTitle>
            <DialogDescription className="text-[#6b6a68]">
              Optional: select the maintenance bay (11–15) where the part is waiting for the installer.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-5 gap-3 pt-2">
            {BAYS.map((bay) => (
              <button key={bay} disabled={busy} onClick={() => move(bay)} data-testid={`maintenance-bay-${bay}`}
                className="min-h-16 rounded-sm border border-amber-400 bg-amber-50 text-lg font-bold text-amber-900 transition-colors hover:bg-amber-400 hover:text-white">
                Bay {bay}
              </button>
            ))}
          </div>
          <Button variant="ghost" disabled={busy} onClick={() => move(null)} data-testid="maintenance-no-bay"
            className="rounded-sm text-xs uppercase tracking-widest text-[#647265] hover:text-[#232826]">
            Move without a bay
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
